import React from 'react';
import { Code, Rocket, CheckCircle2, ExternalLink } from 'lucide-react';
import { projects } from '../data/mock';
import { Badge } from './ui/badge';

const Projects = () => {
  return (
    <section id="projects" className="py-24 bg-gray-950 relative">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <span className="text-gray-500 text-sm font-mono tracking-wider uppercase">What I've Built</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
              Featured Projects
            </h2>
            <div className="w-20 h-1 bg-white mx-auto"></div>
          </div>

          {/* Projects Grid */}
          <div className="space-y-8">
            {projects.map((project, index) => (
              <div
                key={project.id}
                className="bg-black border-2 border-gray-800 rounded-2xl overflow-hidden hover:border-gray-700 transition-all duration-300"
              >
                <div className="p-8">
                  {/* Project Header */}
                  <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="bg-white p-2 rounded-lg">
                          <Code className="w-6 h-6 text-black" />
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-white">{project.title}</h3>
                          <p className="text-gray-500 text-sm">{project.category}</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {project.status === 'Active' ? (
                        <Badge className="bg-green-500/10 text-green-400 border-green-500/20 hover:bg-green-500/20">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Active
                        </Badge>
                      ) : project.status === 'Production' ? (
                        <Badge className="bg-green-500/10 text-green-400 border-green-500/20 hover:bg-green-500/20">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Live in Production
                        </Badge>
                      ) : project.status === 'Concept' ? (
                        <Badge className="bg-gray-500/10 text-gray-400 border-gray-500/20 hover:bg-gray-500/20">
                          <Rocket className="w-3 h-3 mr-1" />
                          Future Concept
                        </Badge>
                      ) : (
                        <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20 hover:bg-blue-500/20">
                          <Rocket className="w-3 h-3 mr-1" />
                          In Development
                        </Badge>
                      )}
                      <span className="text-gray-500 font-mono text-sm">{project.year}</span>
                      {project.note && (
                        <span className="text-gray-600 text-xs italic">({project.note})</span>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-gray-400 text-lg mb-4 leading-relaxed">
                    {project.description}
                  </p>

                  {/* Impact (if available) */}
                  {project.impact && (
                    <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 mb-6">
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="text-gray-400 font-semibold">Impact: </span>
                          <span className="text-gray-300">{project.impact}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Technologies */}
                  <div className="mb-6">
                    <h4 className="text-white font-semibold mb-3 text-sm uppercase tracking-wider">Technologies Used</h4>
                    <div className="flex flex-wrap gap-2">
                      {project.technologies.map((tech, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1.5 bg-gray-900 border border-gray-800 rounded-lg text-gray-300 text-sm font-mono hover:border-gray-700 transition-colors"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Features */}
                  {project.features && (
                    <div>
                      <h4 className="text-white font-semibold mb-3 text-sm uppercase tracking-wider">Key Features</h4>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {project.features.map((feature, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-gray-400 text-sm">
                            <CheckCircle2 className="w-4 h-4 text-gray-600 mt-0.5 flex-shrink-0" />
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Additional note */}
          <div className="mt-12 text-center">
            <p className="text-gray-500 text-sm">
              More projects and contributions on{' '}
              <a href="https://github.com" target="_blank" rel="noopener noreferrer" className="text-white hover:underline inline-flex items-center gap-1">
                GitHub
                <ExternalLink className="w-3 h-3" />
              </a>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Projects;
